/**
 * NOS-193: Donor/contact CSV import wizard.
 *
 * Multi-step dialog: upload → column mapping (auto-detect + presets + manual
 * remap) → validation preview → chunked import with progress → summary with a
 * downloadable skipped-rows CSV. Parsing/mapping/normalization is pure and lives
 * in src/lib/contactImport.ts; the DB write goes through the import-contacts
 * edge function (dedupe/merge/upsert by org_id+email, tags → donor_tags).
 */
import { useMemo, useRef, useState } from "react"
import Papa from "papaparse"
import { FileUp, Loader2, Upload, CheckCircle2, AlertTriangle, Download } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { supabase } from "@/lib/supabase"
import { toast } from "sonner"
import {
  buildMapping,
  normalizeRows,
  skippedRowsToCSV,
  mappingHasEmail,
  CONTACT_FIELD_OPTIONS,
  PRESET_LABELS,
  type ColumnMapping,
  type MappingTarget,
  type PresetName,
  type ImportContactRow,
  type NormalizeResult,
  type SkippedRow,
} from "@/lib/contactImport"

type Step = "upload" | "mapping" | "preview" | "importing" | "summary"

const CHUNK_SIZE = 400

interface ImportSummary {
  created: number
  updated: number
  skipped: SkippedRow[]
}

interface ImportContactsWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  orgId: string | undefined
  onComplete: () => void
}

function downloadTextFile(content: string, filename: string, mime = "text/csv;charset=utf-8;") {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.setAttribute("href", url)
  link.setAttribute("download", filename)
  link.style.visibility = "hidden"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

function formatCents(cents: number): string {
  return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" })
}

export function ImportContactsWizard({
  open,
  onOpenChange,
  orgId,
  onComplete,
}: ImportContactsWizardProps) {
  const [step, setStep] = useState<Step>("upload")
  const [fileName, setFileName] = useState("")
  const [headers, setHeaders] = useState<string[]>([])
  const [rows, setRows] = useState<Record<string, string>[]>([])
  const [preset, setPreset] = useState<PresetName>("auto")
  const [mapping, setMapping] = useState<ColumnMapping>({})
  const [normalized, setNormalized] = useState<NormalizeResult | null>(null)
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [summary, setSummary] = useState<ImportSummary | null>(null)
  const [parsing, setParsing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const reset = () => {
    setStep("upload")
    setFileName("")
    setHeaders([])
    setRows([])
    setPreset("auto")
    setMapping({})
    setNormalized(null)
    setProgress({ done: 0, total: 0 })
    setSummary(null)
    setParsing(false)
  }

  const handleClose = (next: boolean) => {
    if (!next) reset()
    onOpenChange(next)
  }

  const handleFile = (file: File) => {
    setParsing(true)
    setFileName(file.name)
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (h) => h.trim(),
      complete: (results) => {
        const fields = (results.meta.fields ?? []).filter(Boolean)
        const data = results.data.filter((r) => r && Object.keys(r).length > 0)
        if (fields.length === 0 || data.length === 0) {
          toast.error("Could not read any rows from that CSV")
          setParsing(false)
          return
        }
        setHeaders(fields)
        setRows(data)
        setMapping(buildMapping(fields, "auto"))
        setPreset("auto")
        setParsing(false)
        setStep("mapping")
      },
      error: () => {
        toast.error("Failed to parse CSV file")
        setParsing(false)
      },
    })
  }

  const onPresetChange = (value: PresetName) => {
    setPreset(value)
    setMapping(buildMapping(headers, value))
  }

  const onColumnMap = (header: string, target: MappingTarget) => {
    setMapping((prev) => ({ ...prev, [header]: target }))
  }

  const goToPreview = () => {
    const result = normalizeRows(rows, mapping)
    setNormalized(result)
    setStep("preview")
  }

  const hasEmail = useMemo(() => mappingHasEmail(mapping), [mapping])

  const runImport = async () => {
    if (!orgId || !normalized) return
    const valid = normalized.valid
    setStep("importing")
    setProgress({ done: 0, total: valid.length })

    let created = 0
    let updated = 0
    const skipped: SkippedRow[] = [...normalized.skipped]

    for (let i = 0; i < valid.length; i += CHUNK_SIZE) {
      const chunk: ImportContactRow[] = valid.slice(i, i + CHUNK_SIZE)
      try {
        const { data, error } = await supabase.functions.invoke("import-contacts", {
          body: { org_id: orgId, contacts: chunk },
        })
        if (error || !data) {
          for (const c of chunk) {
            skipped.push({ row: c.source_row, email: c.email, reason: `Server error: ${error?.message ?? "unknown"}` })
          }
        } else {
          created += data.created ?? 0
          updated += data.updated ?? 0
          if (Array.isArray(data.skipped)) skipped.push(...data.skipped)
        }
      } catch (err) {
        for (const c of chunk) {
          skipped.push({ row: c.source_row, email: c.email, reason: `Server error: ${(err as Error).message}` })
        }
      }
      setProgress({ done: Math.min(i + CHUNK_SIZE, valid.length), total: valid.length })
    }

    setSummary({ created, updated, skipped })
    setStep("summary")
    onComplete()
    toast.success(`Import complete — ${created} added, ${updated} updated`)
  }

  const downloadSkipped = () => {
    if (!summary) return
    downloadTextFile(skippedRowsToCSV(summary.skipped), `import-skipped-${new Date().toISOString().slice(0, 10)}.csv`)
  }

  const mappedFieldCount = useMemo(
    () => Object.values(mapping).filter((t) => t !== "ignore" && t !== "metadata").length,
    [mapping]
  )
  const metadataCount = useMemo(
    () => Object.values(mapping).filter((t) => t === "metadata").length,
    [mapping]
  )

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import contacts from CSV</DialogTitle>
          <DialogDescription>
            Upload a donor or contact list from Givebutter, Donorbox, or any spreadsheet.
            We&apos;ll match your columns and merge into your existing contacts.
          </DialogDescription>
        </DialogHeader>

        {/* STEP: upload */}
        {step === "upload" && (
          <div className="py-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-muted-foreground/30 p-10 text-center transition hover:border-primary/50 hover:bg-muted/40"
            >
              {parsing ? (
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              ) : (
                <FileUp className="h-8 w-8 text-muted-foreground" />
              )}
              <span className="text-sm font-medium">
                {parsing ? "Reading file…" : "Click to choose a CSV file"}
              </span>
              <span className="text-xs text-muted-foreground">
                Any export works — we map the columns in the next step
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleFile(file)
                e.target.value = ""
              }}
            />
          </div>
        )}

        {/* STEP: mapping */}
        {step === "mapping" && (
          <div className="space-y-4 py-2">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{fileName}</span> · {rows.length} rows ·{" "}
                {mappedFieldCount} fields mapped
                {metadataCount > 0 && `, ${metadataCount} → notes`}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Preset</span>
                <Select value={preset} onValueChange={(v) => onPresetChange(v as PresetName)}>
                  <SelectTrigger className="w-[220px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(PRESET_LABELS) as PresetName[]).map((p) => (
                      <SelectItem key={p} value={p}>
                        {PRESET_LABELS[p]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!hasEmail && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                <span>
                  No column is mapped to <strong>Email</strong>. Rows without an email can still be
                  imported, but they can&apos;t be de-duplicated against existing contacts.
                </span>
              </div>
            )}

            <div className="max-h-[45vh] overflow-y-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>CSV column</TableHead>
                    <TableHead>Sample</TableHead>
                    <TableHead className="w-[230px]">Maps to</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {headers.map((header) => (
                    <TableRow key={header}>
                      <TableCell className="font-medium">{header}</TableCell>
                      <TableCell className="max-w-[180px] truncate text-muted-foreground">
                        {rows[0]?.[header] || "—"}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={mapping[header] ?? "metadata"}
                          onValueChange={(v) => onColumnMap(header, v as MappingTarget)}
                        >
                          <SelectTrigger className="w-[210px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CONTACT_FIELD_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                            <SelectItem value="metadata">Save to notes (metadata)</SelectItem>
                            <SelectItem value="ignore">Ignore this column</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* STEP: preview */}
        {step === "preview" && normalized && (
          <div className="space-y-4 py-2">
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="rounded-md bg-emerald-500/10 px-3 py-1.5 font-medium text-emerald-700 dark:text-emerald-400">
                {normalized.valid.length} ready to import
              </span>
              {normalized.noEmailCount > 0 && (
                <span className="rounded-md bg-amber-500/10 px-3 py-1.5 font-medium text-amber-700 dark:text-amber-400">
                  {normalized.noEmailCount} without email
                </span>
              )}
              {normalized.skipped.length > 0 && (
                <span className="rounded-md bg-red-500/10 px-3 py-1.5 font-medium text-red-700 dark:text-red-400">
                  {normalized.skipped.length} skipped
                </span>
              )}
            </div>

            {normalized.valid.length > 0 && (
              <div className="max-h-[40vh] overflow-y-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Tags</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {normalized.valid.slice(0, 10).map((c, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{c.name || "—"}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {c.no_email ? <span className="italic">no email</span> : c.email}
                        </TableCell>
                        <TableCell className="text-right">{formatCents(c.total_donated)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {c.tags.slice(0, 3).map((t) => (
                              <Badge key={t} variant="secondary" className="text-xs">
                                {t}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {normalized.skipped.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Skipped rows (e.g. {normalized.skipped[0].reason}) are listed with reasons in the
                summary and can be downloaded after import.
              </p>
            )}
          </div>
        )}

        {/* STEP: importing */}
        {step === "importing" && (
          <div className="space-y-4 py-8">
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm font-medium">
                Importing… {progress.done} / {progress.total}
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}

        {/* STEP: summary */}
        {step === "summary" && summary && (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="h-6 w-6" />
              <span className="text-lg font-semibold">Import complete</span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-md border p-3">
                <div className="text-2xl font-semibold">{summary.created}</div>
                <div className="text-xs text-muted-foreground">Created</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-2xl font-semibold">{summary.updated}</div>
                <div className="text-xs text-muted-foreground">Updated</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-2xl font-semibold">{summary.skipped.length}</div>
                <div className="text-xs text-muted-foreground">Skipped</div>
              </div>
            </div>
            {summary.skipped.length > 0 && (
              <Button variant="outline" size="sm" onClick={downloadSkipped} className="w-full">
                <Download className="mr-2 h-4 w-4" />
                Download {summary.skipped.length} skipped rows (with reasons)
              </Button>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === "mapping" && (
            <>
              <Button variant="outline" onClick={() => setStep("upload")}>
                Back
              </Button>
              <Button onClick={goToPreview} disabled={mappedFieldCount === 0}>
                Preview import
              </Button>
            </>
          )}
          {step === "preview" && (
            <>
              <Button variant="outline" onClick={() => setStep("mapping")}>
                Back
              </Button>
              <Button onClick={runImport} disabled={!normalized || normalized.valid.length === 0}>
                <Upload className="mr-2 h-4 w-4" />
                Import {normalized?.valid.length ?? 0} contacts
              </Button>
            </>
          )}
          {step === "summary" && (
            <Button onClick={() => handleClose(false)}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
